import { Link } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import type { Breakpoint, SxProps, Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { useGetByDevice } from '@onesource/components';
import { RycLogo } from '@onesource/icons';
import { AuthProviderEnum } from '@onesource/schemas';
import { getAuthProvider, getSession } from '@onesource/services';
import { useMemo, useState } from 'react';
import { MdAdminPanelSettings, MdCreditCard, MdHome, MdMonitor } from 'react-icons/md';
import { WP_MEMBERS_SITE } from 'src/config';

import { layoutClasses } from '../classes';
import { AccountPopover } from '../components/account-popover';
import { ContactPopover } from '../components/contact-popover';
import { MenuButton } from '../components/menu-button';
import { NotificationCenter } from '../components/notification-center';
import { HeaderSection } from '../core/header-section';
import { LayoutSection } from '../core/layout-section';
import { Sidebar } from '../sidebar';
import { MobileNavDialog } from '../sidebar/mobile-nav-dialog';
import { Main } from './main';

// ----------------------------------------------------------------------

export type DashboardLayoutProps = {
  sx?: SxProps<Theme>;
  children: React.ReactNode;
  header?: {
    sx?: SxProps<Theme>;
  };
};

export function DashboardLayout({ sx, children, header }: DashboardLayoutProps) {
  const theme = useTheme();
  const [navOpen, setNavOpen] = useState(false);
  const { isMobile } = useGetByDevice();

  const layoutQuery: Breakpoint = 'lg';

  const accountPopoverData = useMemo(() => {
    const items: Array<{
      label: string;
      href: string;
      icon?: React.ReactNode;
      onClick?: () => void;
    }> = [
      {
        label: 'Home',
        href: '/',
        icon: <MdHome size={22} />,
      },
    ];

    if (getAuthProvider() === AuthProviderEnum.wordpress) {
      items.push({
        label: 'Dashboard',
        href: '#',
        icon: <MdMonitor size={22} />,
        onClick: () => {
          const token = getSession();
          window.location.href = `${WP_MEMBERS_SITE}/verify-token?token=${token}&redirect_url=${WP_MEMBERS_SITE}/#/`;
        },
      });
    }

    items.push({
      label: 'My Account',
      href: '/account',
      icon: <MdAdminPanelSettings size={22} />,
    });

    items.push({
      label: 'Billing Details',
      href: '/billing',
      icon: <MdCreditCard size={22} />,
    });

    return items;
  }, []);

  return (
    <LayoutSection
      /** **************************************
       * Header
       *************************************** */
      headerSection={
        <HeaderSection
          layoutQuery={layoutQuery}
          slotProps={{
            container: {
              maxWidth: false,
              sx: {
                px: {
                  [layoutQuery]: 5,
                },
              },
            },
          }}
          sx={{ height: { xs: 64, md: 84 }, ...header?.sx }}
          slots={{
            topArea: (
              <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
                This is an info Alert.
              </Alert>
            ),
            leftArea: (
              <>
                <Link
                  href="https://restoreyourcore.com"
                  style={{
                    height: isMobile ? '36px' : '43px',
                    color: theme.palette.primary.main,
                  }}
                >
                  <RycLogo
                    color={theme.palette.primary.light}
                    width={isMobile ? '82px' : '100px'}
                    height={isMobile ? '36px' : '43px'}
                  />
                </Link>
                <MobileNavDialog open={navOpen} onClose={() => setNavOpen(false)} />
              </>
            ),
            rightArea: (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                  height: { xs: 37, md: 50 },
                  background: 'white',
                  p: { xs: '6px 10px', md: '0px 20px' },
                  borderRadius: 8,
                  minWidth: 'fit-content',
                }}
              >
                {/* <Searchbar /> */}
                <ContactPopover />
                <NotificationCenter />
                <AccountPopover data={accountPopoverData} />
                <MenuButton
                  onClick={() => setNavOpen(true)}
                  sx={{
                    ml: { xs: 0.8, md: 0 },
                    p: { xs: 0.5, md: 0 },
                    [theme.breakpoints.up(layoutQuery)]: { display: 'none' },
                  }}
                />
              </Box>
            ),
          }}
        />
      }
      sidebarSection={<Sidebar />}
      footerSection={null}
      sx={{
        [`& .${layoutClasses.hasSidebar}`]: {
          [theme.breakpoints.up(layoutQuery)]: {
            pl: '280px',
          },
        },
        ...sx,
      }}
    >
      <Main>{children}</Main>
    </LayoutSection>
  );
}
